import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  List,
  ListItem,
  ListSeparator,
  EditIcon,
  PeopleIcon,
  ArrowLeftBoxIcon,
  NotificationBellIcon,
  BellOffIcon,
  PersonPlusIcon,
  TrashIcon,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { types } from "./ConversationContent/types/index.jsx";
import { ConversationRenameForm } from "./ConversationRenameForm.jsx";
import { useConversationContext } from "../context/ConversationContext.jsx";
import { useConversationContentContext } from "../context/index.jsx";
import { useConfirmDialogOption, useInviteOption, DeleteOptionDialog } from "../MenuOptions/index.jsx";

export const ConversationMenuOptions = ({ onLeaveSuccess, onOptionClick }) => {
  const {
    conversation = {},
    leave,
    toggleMuted,
    deleteConversation,
    isMuted = false,
    canManage = false,
  } = useConversationContext();
  const { t } = useTranslation();
  const { setScreen } = useConversationContentContext();
  const { class: classType } = conversation;
  const type = types[classType];
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleOpenRenameDialog = useCallback(() => {
    setShowRenameDialog(true);
  }, []);

  const handleCloseRenameDialog = useCallback(() => {
    setShowRenameDialog(false);
  }, []);

  const handleOpenDeleteDialog = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const handleSubmitRenameDialog = useCallback(() => {
    setShowRenameDialog(false);
    typeof onOptionClick === "function" && onOptionClick();
  }, [onOptionClick]);

  const handleSubmitDeleteDialog = useCallback(() => {
    typeof deleteConversation === "function" && deleteConversation();
    typeof onLeaveSuccess === "function" && onLeaveSuccess();
  }, [deleteConversation, onLeaveSuccess]);

  const handleToggleMuted = useCallback(() => {
    typeof toggleMuted === "function" && toggleMuted();
    typeof onOptionClick === "function" && onOptionClick();
  }, [onOptionClick, toggleMuted]);

  const handleOpenParticipants = useCallback(() => {
    typeof setScreen === "function" && setScreen("participants");
    typeof onOptionClick === "function" && onOptionClick();
  }, [setScreen, onOptionClick]);

  const [onShowLeaveDialog, leaveDialogTemplate] = useConfirmDialogOption({
    onConfirmSuccess: onLeaveSuccess,
    onConfirmAction: leave,
  });

  const { onShowInviteDialog, inviteTemplate } = useInviteOption(onOptionClick);

  return (
    <>
      <List className="tw-py-2">
        {type === "GroupConversation" && canManage ? (
          <>
            <ListItem onClick={handleOpenRenameDialog}>
              <EditIcon className="tw-mr-2 fill-900" /> {t("Rename")}
            </ListItem>
            <ListItem onClick={onShowInviteDialog}>
              <PersonPlusIcon className="tw-mr-2 fill-900" /> {t("Invite")}
            </ListItem>
          </>
        ) : null}
        {type === "GroupConversation" || type === "GeneralConversation" || type === "ParentObjectConversation" ? (
          <ListItem onClick={handleOpenParticipants}>
            <PeopleIcon className="tw-mr-2 fill-900" /> {t("Participants")}
          </ListItem>
        ) : null}
        <ListItem onClick={handleToggleMuted}>
          {isMuted ? (
            <>
              <NotificationBellIcon className="tw-mr-2 fill-900" />
              {t("Unmute")}{" "}
            </>
          ) : (
            <>
              <BellOffIcon className="tw-mr-2 fill-900" /> {t("Mute")}
            </>
          )}
        </ListItem>
        {type === "GroupConversation" ? (
          <>
            <ListSeparator />
            <ListItem onClick={onShowLeaveDialog}>
              <ArrowLeftBoxIcon className="tw-mr-2 fill-900" /> {t("Leave")}
            </ListItem>
            {canManage ? (
              <ListItem onClick={handleOpenDeleteDialog}>
                <TrashIcon className="tw-mr-2 fill-900" /> {t("Delete")}
              </ListItem>
            ) : null}
          </>
        ) : null}
      </List>
      {leaveDialogTemplate}
      <DeleteOptionDialog
        open={showDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleSubmitDeleteDialog}
      />
      <Dialog
        open={showRenameDialog}
        onClose={handleCloseRenameDialog}
      >
        <ConversationRenameForm
          onCancel={handleCloseRenameDialog}
          onSubmit={handleSubmitRenameDialog}
        />
      </Dialog>
      {inviteTemplate}
    </>
  );
};

ConversationMenuOptions.propTypes = {
  onOptionClick: PropTypes.func,
  onLeaveSuccess: PropTypes.func,
};
